/**
 * Canonical event names — all event strings live here, nowhere else.
 * "Object Verb" past tense, Title Case. Mixpanel best-practice for reports.
 *
 * Ported 1:1 from the legacy CRA app (`src/app/utils/analytics.js`). Downstream
 * code depends on these literal string values — do NOT rename them.
 *
 * `as const` makes every value a literal type so `EVENTS.SIGNED_IN` is typed as
 * the exact string `'Signed In'` rather than `string`.
 */
export const EVENTS = {
  // Lifecycle
  PAGE_VIEWED: 'Page Viewed',
  APP_ERROR_SHOWN: 'App Error Shown',
  NOT_FOUND_VIEWED: '404 Viewed',
  CONTACT_FORM_SUBMITTED: 'Contact Form Submitted',

  // Auth
  SIGNED_UP: 'Signed Up',
  SIGNED_IN: 'Signed In',
  SIGNED_OUT: 'Signed Out',
  PASSWORD_RESET_VERIFIED: 'Password Reset Verified',
  PROFILE_COMPLETION_SUBMITTED: 'Profile Completion Submitted',
  NOTIFICATION_PROMPT_SHOWN: 'Notification Prompt Shown',
  NOTIFICATION_PROMPT_ANSWERED: 'Notification Prompt Answered',

  // Posting
  POST_CREATED: 'Post Created',
  POST_DELETED: 'Post Deleted',

  // Engagement
  POST_LIKED: 'Post Liked',
  POST_UNLIKED: 'Post Unliked',
  POST_COMMENTED: 'Post Commented',
  POST_SHARE_OPENED: 'Post Share Opened',
  POST_SHARED: 'Post Shared',
  POST_REPORTED: 'Post Reported',
  HASHTAG_CLICKED: 'Hashtag Clicked',
  FOLLOW_ACTION: 'Follow Action',
  USER_BLOCKED: 'User Blocked',
  USER_UNBLOCKED: 'User Unblocked',
  SEARCH_PERFORMED: 'Search Performed',
  SEARCH_RESULT_CLICKED: 'Search Result Clicked',
  NOTIFICATION_CLICKED: 'Notification Clicked',
  FEED_PAGINATED: 'Feed Paginated',

  // Media
  GALLERY_SWIPED: 'Gallery Swiped',
  VIDEO_PLAYED: 'Video Played',
  VIDEO_PROGRESS: 'Video Progress',
  VIDEO_COMPLETED: 'Video Completed',

  // Livestream
  STREAM_STARTED: 'Stream Started',
  STREAM_JOINED: 'Stream Joined',
  STREAM_LEFT: 'Stream Left',
  STREAM_CHAT_SENT: 'Stream Chat Sent',

  // Monetization
  SUBSCRIPTION_MODAL_SHOWN: 'Subscription Modal Shown',
  PLAN_SELECTED: 'Plan Selected',
  CHECKOUT_STARTED: 'Checkout Started',
  SUBSCRIPTION_PURCHASED: 'Subscription Purchased',
  CHECKOUT_FAILED: 'Checkout Failed',
  SUBSCRIPTION_CANCELLED: 'Subscription Cancelled',

  // Profile
  PROFILE_UPDATED: 'Profile Updated',
  AVATAR_UPLOADED: 'Avatar Uploaded',
  COVER_UPLOADED: 'Cover Uploaded',
  PRIVACY_SETTING_CHANGED: 'Privacy Setting Changed',

  // Referral
  REFERRAL_LINK_OPENED: 'Referral Link Opened',
  REFERRAL_SHARED: 'Referral Shared',

  // Outbound — external URLs the user navigates away to (news source links,
  // profile social/website, anchors inside blog HTML bodies, etc.). Source
  // discriminator goes in the event's `source` property.
  EXTERNAL_LINK_CLICKED: 'External Link Clicked',

  // Ads (optional — only if AdSense ToS allows)
  AD_IMPRESSION: 'Ad Impression',
} as const;

/** Union of the EVENTS object's keys, e.g. `'SIGNED_IN' | 'POST_CREATED' | ...`. */
export type EventKey = keyof typeof EVENTS;

/** Union of the canonical event-name string literals, e.g. `'Signed In' | ...`. */
export type EventName = (typeof EVENTS)[EventKey];
